import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetClientQuery } from "@generated";
import { ClientForm } from "../ClientForm/ClientForm";
import { useClientMutations, useClientFormData } from "../api";
import { emptyClientForm } from "../types";
import type { ClientFormValues } from "../types";

const FORM_ID = "client-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = useClientMutations();
  const {
    rateTables,
    projectVariables,
    isLoading: formDataLoading,
  } = useClientFormData();
  const {
    data,
    isLoading: clientLoading,
    refetch,
  } = useV2GetClientQuery(
    {
      id: Number(id),
      include: ["rate-table"],
    },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<ClientFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadedInitial = useMemo<ClientFormValues>(() => {
    const attrs = data?.data?.attributes;
    if (!attrs) return emptyClientForm;
    const udfValues: Record<string, string> = {};
    (attrs["user-defined-fields"] ?? []).forEach(
      (u: { name: string; value?: string }) => {
        udfValues[u.name] = u.value ?? "";
      }
    );
    const rateTableId = (
      data?.data?.relationships?.["rate-table"]?.data as
        | { id?: number }
        | undefined
    )?.id;
    return {
      name: attrs.name ?? "",
      domain: attrs.domain ?? "",
      msaDate: attrs["msa-date"] ?? "",
      rateTableId: rateTableId ? String(rateTableId) : "",
      udfValues,
    };
  }, [data]);

  if (clientLoading || formDataLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: ClientFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values, projectVariables);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update client", err);
      setToast({ variant: "danger", text: "Could not save client." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Client"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <ClientForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          rateTables={rateTables}
          projectVariables={projectVariables}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
